import { useCallback, useEffect, useState } from "react";

interface UseDeadlineResult {
  /** Absolute deadline (ms epoch), or `undefined` when inactive. Stable between start/clear. */
  deadline: number | undefined;
  /** `true` once the deadline has passed, until `clear` (or the next `start`). */
  hasElapsed: boolean;
  /** (Re)starts the window: a deadline `durationSeconds` from now. */
  start: () => void;
  /** Cancels the window and resets state. */
  clear: () => void;
}

/**
 * Tracks a single future deadline as a boolean `hasElapsed`, re-rendering the
 * caller only when it flips (or on start/clear) — a single timeout rather than a
 * per-second countdown. `deadline` is exposed so a display-only leaf can tick the
 * remaining seconds without re-rendering the caller (see `ResendCountdown`).
 *
 * A backgrounded webview throttles timers, so the timeout can fire late; the
 * `visibilitychange` re-check (recomputed from the wall clock) flips the flag
 * immediately on resume.
 */
export function useDeadline(durationSeconds: number): UseDeadlineResult {
  const [deadline, setDeadline] = useState<number | undefined>();
  const [hasElapsed, setHasElapsed] = useState(false);

  useEffect(() => {
    if (deadline === undefined) {
      return undefined;
    }

    function check(): void {
      if (Date.now() >= deadline!) {
        setHasElapsed(true);
      }
    }

    const timeoutId = window.setTimeout(check, Math.max(0, deadline - Date.now()));
    document.addEventListener("visibilitychange", check);
    // Recompute immediately in case we mounted/resumed already past the deadline.
    check();

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", check);
    };
  }, [deadline]);

  const start = useCallback(() => {
    setHasElapsed(false);
    setDeadline(Date.now() + durationSeconds * 1000);
  }, [durationSeconds]);

  const clear = useCallback(() => {
    setHasElapsed(false);
    setDeadline(undefined);
  }, []);

  return { deadline, hasElapsed, start, clear };
}
